/**
 * Step-gating logic for the customer-deactivation wizard.
 *
 * Deactivating a customer is staff's permanent "delete": a three-step confirm
 * flow (review the preflight summary, choose what happens to the customer's
 * radios — relocate to a destination, or purge — then type the customer name
 * to confirm). A relocate destination is EITHER a global inventory location
 * or another active customer, mutually exclusive. This module holds the pure
 * decisions that gate progression through those steps so the dialog stays
 * presentational and the branchy logic is unit-testable.
 *
 * Contains:
 * - RADIO_ACTIONS — the two allowed radio dispositions (matches the BE enum)
 * - DESTINATION_MODES — the two relocate targets (inventory location / customer)
 * - STEPS — the ordered wizard step keys
 * - isValidRadioAction — radio-choice guard
 * - isValidDestinationId / defaultDestinationId — inventory-location picker
 * - isRelocateAvailable — whether any relocate destination is offered
 * - isValidDestinationCustomerId — destination-customer guard (exists/self)
 * - canProceedFromRadioStep — the radio-step advance gate
 * - isConfirmationMatch — the type-to-confirm name matcher
 * - nextStep / prevStep — ordered step navigation
 *
 * Returns plain JS — no JSX, no redux.
 */

export const RADIO_ACTIONS = Object.freeze({ INVENTORY: 'inventory', PURGE: 'purge' });

export const DESTINATION_MODES = Object.freeze({ LOCATION: 'location', CUSTOMER: 'customer' });

export const STEPS = Object.freeze(['summary', 'radio', 'confirm']);

/**
 * @param {string} action - a radio-disposition choice.
 * @returns {boolean} true when it is one of the allowed radio actions.
 */
export function isValidRadioAction(action) {
  return action === RADIO_ACTIONS.INVENTORY || action === RADIO_ACTIONS.PURGE;
}

/**
 * @param {number} [destinationId] - a chosen inventory-location id.
 * @param {Array<{id: number}>} [destinations] - the preflight's radio_destinations.
 * @returns {boolean} true when destinationId names one of the offered destinations.
 */
export function isValidDestinationId(destinationId, destinations) {
  return (destinations ?? []).some(destination => destination.id === destinationId);
}

/**
 * @param {Array<{id: number, is_default: boolean}>} [destinations] - the
 *   preflight's radio_destinations.
 * @returns {number|null} the id of the destination flagged is_default (the
 *   ODL warehouse), or null when none is flagged.
 */
export function defaultDestinationId(destinations) {
  return destinations?.find(destination => destination.is_default)?.id ?? null;
}

/**
 * @param {Array<{id: number}>} [destinations] - the preflight's radio_destinations.
 * @returns {boolean} true when at least one relocate destination is offered.
 *   An older backend omits the list; the relocate option is then unusable and
 *   the dialog forces a purge rather than showing an empty picker.
 */
export function isRelocateAvailable(destinations) {
  return Array.isArray(destinations) && destinations.length > 0;
}

/**
 * @param {number} [destinationCustomerId] - the picked destination customer id.
 * @param {number} [deactivatingCustomerId] - the customer being deactivated.
 * @returns {boolean} true when destinationCustomerId is a real customer id that
 *   is not the customer being deactivated. Existence/active state is the BE's
 *   authority; this gate only rejects an unset id and the self target.
 */
export function isValidDestinationCustomerId(destinationCustomerId, deactivatingCustomerId) {
  return (
    Number.isInteger(destinationCustomerId) &&
    destinationCustomerId > 0 &&
    destinationCustomerId !== deactivatingCustomerId
  );
}

/**
 * @param {object} params
 * @param {string} params.radioAction - the chosen radio disposition.
 * @param {string} [params.destinationMode] - the relocate target (inventory-location or customer).
 * @param {number} [params.destinationId] - the chosen inventory-location id (location mode).
 * @param {number} [params.destinationCustomerId] - the chosen destination customer id (customer mode).
 * @param {Array<{id: number}>} [params.destinations] - the preflight's radio_destinations.
 * @param {number} [params.deactivatingCustomerId] - the customer being deactivated.
 * @returns {boolean} true when the radio step may advance: a valid action, and
 *   — for inventory — a complete destination for whichever mode is chosen.
 */
export function canProceedFromRadioStep({
  radioAction,
  destinationMode,
  destinationId,
  destinationCustomerId,
  destinations,
  deactivatingCustomerId,
}) {
  if (!isValidRadioAction(radioAction)) {
    return false;
  }
  if (radioAction === RADIO_ACTIONS.PURGE) {
    return true;
  }
  if (destinationMode === DESTINATION_MODES.CUSTOMER) {
    return isValidDestinationCustomerId(destinationCustomerId, deactivatingCustomerId);
  }
  return isValidDestinationId(destinationId, destinations);
}

/**
 * The type-to-confirm matcher. Requires an exact (case-sensitive) match of
 * the customer name after trimming surrounding whitespace, and rejects an
 * empty target so a blank name never auto-confirms.
 *
 * @param {string} typed - what the operator typed.
 * @param {string} customerName - the customer's canonical name.
 * @returns {boolean} true when the typed value confirms the deactivation.
 */
export function isConfirmationMatch(typed, customerName) {
  const target = (customerName ?? '').trim();
  if (target.length === 0) {
    return false;
  }
  return (typed ?? '').trim() === target;
}

/**
 * @param {string} step - the current step key.
 * @returns {string} the next step key, or the same key at the end.
 */
export function nextStep(step) {
  const index = STEPS.indexOf(step);
  if (index < 0 || index === STEPS.length - 1) {
    return step;
  }
  return STEPS[index + 1];
}

/**
 * @param {string} step - the current step key.
 * @returns {string} the previous step key, or the same key at the start.
 */
export function prevStep(step) {
  const index = STEPS.indexOf(step);
  if (index <= 0) {
    return step;
  }
  return STEPS[index - 1];
}
